/**
 * Admin Roles & Permissions — two-tab management panel
 *
 * Tab 1 — Project Role Permissions
 *   Left: role list (all roles: system + custom project roles)
 *   Right: permission toggles for selected role, filtered by project type
 *   Scales to any number of roles without breaking the layout
 *
 * Tab 2 — Page & Module Access
 *   Matrix of pages vs system roles (Admin/Manager/Specialist)
 *   Toggle cells control which roles can see which pages
 */
import { useState, useEffect, useCallback } from 'react';
import { pageAccessAPI, projectRolesAPI } from '../../utils/api';
import { ALL_ROLES } from '../../utils/rolesConfig';

import { useToast, apiError, useConfirm } from '../../components/Toast';
import { Lock, Plus } from 'lucide-react';

// ── Icons ──────────────────────────────────────────────────────────────────
const PlusIc = () => <Plus size={13} />;

const PROJECT_TYPES = ['General','Sales','Customer Onboarding','Implementation'];

const PERM_CATEGORIES = {
  project:   'Project Management',
  workflow:  'Workflow & Stages',
  financial: 'Financial',
  team:      'Team Management',
  content:   'Content Creation',
};

// Permission level columns for page-access matrix (4 levels, not 13 individual roles)
const PERM_LEVELS = ['SystemAdmin','Admin','Executive','Manager','Specialist','Developer'];
const PERM_LEVEL_COLOR = { Admin:'#ef4444', Manager:'#0891b2', Specialist:'#10b981', Developer:'#2E539F' };
// All 13 individual roles - used in Tab 1 role list
const SYSTEM_ROLES = ALL_ROLES.map(r => r.value);
// ROLE_COLORS from rolesConfig

// ── Toggle switch ──────────────────────────────────────────────────────────
const Toggle = ({ on, onChange, disabled, title }) => (
  <button onClick={() => !disabled && onChange(!on)} disabled={disabled} title={title}
    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
    style={{ background: on ? '#2E539F' : 'var(--border)' }}>
    <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
      style={{ left: on ? 18 : 2 }}/>
  </button>
);

// ── Main component ─────────────────────────────────────────────────────────
export default function AdminRolesPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const [roles,        setRoles]        = useState([]);
  const [permissions,  setPermissions]  = useState([]);
  const [pages,        setPages]        = useState([]);
  const [selectedRole, setSelectedRole] = useState('Admin'); // stores ALL_ROLES value e.g. 'CEO'

  // Derive: base permission level for selected role
  const selectedRoleObj  = ALL_ROLES.find(r => r.value === selectedRole);
  const selectedPermLevel = selectedRoleObj?.permLevel || 'Admin';
  // Find the DB system role row that matches this permission level (for permission API calls)
  // Executive level maps to Manager in DB for permission lookup
  const dbPermLevel = selectedPermLevel === 'Executive' ? 'Manager' : selectedPermLevel;
  const baseDbRole = roles.find(r => r.name === dbPermLevel && r.is_system);
  const [activeType,   setActiveType]   = useState('General');
  const [saving,       setSaving]       = useState({});
  const [moduleFilter, setModuleFilter] = useState('All');
  const [levelFilter,  setLevelFilter]  = useState('Manager');
  const [loading,      setLoading]      = useState(true);
  const [showNewRole,  setShowNewRole]  = useState(false);
  const [newName,      setNewName]      = useState('');
  const [newDesc,      setNewDesc]      = useState('');
  const [creating,     setCreating]     = useState(false);
  const { show, Toast } = useToast();

  // Load everything on mount
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, pRes, pgRes] = await Promise.all([
        projectRolesAPI.getDefinitions(activeType),
        projectRolesAPI.getPermissions(),
        pageAccessAPI.getAll(),
      ]);
      const loadedRoles = rRes.data || [];
      setRoles(loadedRoles);
      setPermissions(pRes.data || []);
      setPages(pgRes.data || []);
      // Auto-select first role if none selected
      // Trigger permission load for default selected role
      if (loadedRoles.length > 0) setSelectedRole(prev => prev || 'Admin');
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [activeType]); // loadAll stable — selectedRole intentionally excluded to prevent loop

  useEffect(() => { loadAll(); }, [activeType]);

  const currentRole = baseDbRole; // permissions come from the base permission level

  const hasGrant = (role, permCode) =>
    (role?.permissions || []).some(p =>
      p.permission_code === permCode &&
      (p.project_type === activeType || p.project_type === null || !p.project_type)
    );

  // Toggle a project permission — Admin has full control over all roles
  const togglePermission = async (role, permCode, currentlyOn) => {
    const key = role.id + permCode;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await projectRolesAPI.updateGrant(role.id, {
        permission_code: permCode,
        project_type:    activeType,
        action:          currentlyOn ? 'revoke' : 'grant',
      });
      // Optimistic local update
      setRoles(prev => prev.map(r => {
        if (r.id !== role.id) return r;
        const perms = r.permissions || [];
        return {
          ...r,
          permissions: currentlyOn
            ? perms.filter(p => !(p.permission_code === permCode &&
                (p.project_type === activeType || p.project_type === null)))
            : [...perms, { permission_code: permCode, project_type: activeType }],
        };
      }));
      show(currentlyOn ? 'Permission revoked' : 'Permission granted');
    } catch(e) {
      show(e.response?.data?.message || 'Could not update permission', true);
    } finally {
      setSaving(s => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  // Toggle page access
  const togglePageAccess = async (pageId, roleName, currentlyOn, type='access') => {
    if (roleName === 'Admin') {
      show('Admin access cannot be restricted', true); return;
    }
    const key = pageId + roleName + type;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await pageAccessAPI.toggle(pageId, roleName, type, !currentlyOn);
      setPages(prev => prev.map(pg => {
        if (pg.id !== pageId) return pg;
        const cur = pg.access?.[roleName];
        const obj = typeof cur === 'object' && cur !== null
          ? { ...cur }
          : { on: typeof cur === 'boolean' ? cur : false, read:true, write:false, update:false };
        if (type === 'access') obj.on     = !currentlyOn;
        if (type === 'read')   obj.read   = !currentlyOn;
        if (type === 'write')  obj.write  = !currentlyOn;
        if (type === 'update') obj.update = !currentlyOn;
        return { ...pg, access: { ...pg.access, [roleName]: obj } };
      }));
      show((!currentlyOn ? 'Access granted' : 'Access revoked') + ' for ' + roleName);
    } catch(e) {
      show(e.response?.data?.message || 'Could not update access', true);
    } finally {
      setSaving(s => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  const createRole = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await projectRolesAPI.createRole({ name: newName.trim(), description: newDesc.trim() || null });
      setShowNewRole(false); setNewName(''); setNewDesc('');
      show('Role created');
      loadAll();
    } catch(e) {
      show(e.response?.data?.message || 'Could not create role', true);
    } finally { setCreating(false); }
  };

  const deleteRole = async (roleId, roleName) => {
    if (!await confirm(`Delete "${roleName}"? All permission grants and user assignments will be removed.`)) return;
    try {
      await projectRolesAPI.deleteRole(roleId);
      if (selectedRole === roleId) setSelectedRole(null);
      show('Role deleted');
      loadAll();
    } catch(e) { show(e.response?.data?.message || 'Could not delete', true); }
  };

  // Group permissions by category
  const permsByCategory = permissions.reduce((acc, p) => {
    const cat = p.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  // Group pages by module group
  const pagesByGroup = pages.reduce((acc, p) => {
    if (!acc[p.module_group]) acc[p.module_group] = [];
    acc[p.module_group].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <h1 className="page-title">Roles & Permissions</h1>
        <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
          Manage project-level permission schemes and system-wide page access for each role.
        </p>
      </div>

      {/* Top tabs */}
      <div className="flex border-b" style={{ borderColor:'var(--border)' }}>
        <button className="px-5 py-3 text-sm font-medium border-b-2 border-brand-500 -mb-px"
            style={{ color:'#2E539F' }}>
            Project Role Permissions
          </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
        </div>
      ) : (

        <>
          {/* ── TAB 1: Project Role Permissions ─────────────────────────── */}
          {(
            <div className="flex gap-5">

              {/* Left: role list */}
              <div className="w-56 flex-shrink-0 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>Roles</p>
                  <button onClick={() => setShowNewRole(true)}
                    className="flex items-center gap-1 text-xs text-brand-500 hover:underline">
                    <PlusIc /> New
                  </button>
                </div>

                {/* Flat role list with inline level badge — no section headers */}
                {ALL_ROLES.filter(r => r.value !== 'Specialist').map(role => {
                  const lvColors = {
                    SystemAdmin:'#2E539F', Admin:'#2E539F', Executive:'#8b5cf6',
                    Manager:'#0891b2', Specialist:'#10b981', Developer:'#8b5cf6',
                  };
                  const lvColor = lvColors[role.permLevel] || '#64748b';
                  const isSelected = selectedRole === role.value;
                  return (
                    <button key={role.value} onClick={() => setSelectedRole(role.value)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-colors mb-0.5
                        ${isSelected ? 'bg-brand-500/15' : 'hover:bg-[var(--surface-alt)]'}`}>
                      <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: lvColor }}>
                        {(role.value||'?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs leading-tight truncate" style={{ color: isSelected ? '#2E539F' : 'var(--text)' }}>
                          {role.label}
                        </p>
                        <p className="text-[10px] leading-tight mt-0.5" style={{ color: lvColor }}>
                          {role.permLevel}
                        </p>
                      </div>
                      {role.value === 'Admin' && <Lock size={11}/>}
                    </button>
                  );
                })}

                {/* New role form */}
                {showNewRole && (
                  <div className="card mt-2 p-3 space-y-2">
                    <p className="text-xs font-semibold" style={{ color:'var(--text)' }}>New Role</p>
                    <input className="input text-xs py-1.5" placeholder="Role name *" value={newName}
                      onChange={e => setNewName(e.target.value)} autoFocus
                      onKeyDown={e => e.key === 'Enter' && createRole()}/>
                    <input className="input text-xs py-1.5" placeholder="Description" value={newDesc}
                      onChange={e => setNewDesc(e.target.value)}/>
                    <div className="flex gap-1.5">
                      <button onClick={createRole} disabled={creating || !newName.trim()}
                        className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1">
                        {creating && <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin"/>}
                        Create
                      </button>
                      <button onClick={() => { setShowNewRole(false); setNewName(''); setNewDesc(''); }}
                        className="btn-secondary text-xs px-2.5 py-1.5">Cancel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: permissions for selected role */}
              <div className="flex-1 min-w-0">
                {!currentRole ? (
                  <div className="card text-center py-12" style={{ color:'var(--text-secondary)' }}>
                    Select a role from the list to manage its permissions
                  </div>
                ) : (
                  <div className="card overflow-hidden p-0">
                    {/* Role header */}
                    <div className="px-5 py-4 border-b" style={{ borderColor:'var(--border)' }}>
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <div>
                              <h3 className="font-semibold" style={{ color:'var(--text)' }}>{selectedRoleObj?.label || selectedRole}</h3>
                              <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>
                                Permission level: <span className="font-medium" style={{ color:selectedPermLevel==='Admin'?'#ef4444':selectedPermLevel==='Manager'?'#0891b2':selectedPermLevel==='Specialist'?'#10b981':'#2E539F' }}>{selectedPermLevel}</span>
                                {selectedRoleObj?.permLevel !== selectedRole && <> · Permissions inherited from <strong>{selectedRoleObj?.permLevel}</strong> level</>}
                              </p>
                            </div>
                          </div>

                          {selectedRole === 'Admin' && (
                            <p className="text-xs mt-1 text-amber-600">
                              Admin always has full access to everything by default. You can still restrict individual permissions below.
                            </p>
                          )}
                          {selectedPermLevel !== 'Admin' && (
                            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                              These are the default permissions for all <strong>{selectedPermLevel}</strong>-level users ({selectedRoleObj?.label} and equivalent roles).
                              Adjust to control what they can do across all projects.
                            </p>
                          )}
                        </div>
                        {/* Project type filter for this role */}
                        <div className="flex gap-1 flex-wrap">
                          {PROJECT_TYPES.map(type => (
                            <button key={type} onClick={() => setActiveType(type)}
                              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${activeType === type ? 'text-white' : 'hover:bg-[var(--surface-alt)]'}`}
                              style={{
                                background: activeType === type ? '#2E539F' : 'transparent',
                                color: activeType === type ? '#fff' : 'var(--text-secondary)',
                              }}>
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Permission rows — Admin always has full access */}
                    <div className="divide-y" style={{ borderColor:'var(--border)' }}>
                      {Object.entries(permsByCategory).map(([cat, perms]) => (
                        <div key={cat}>
                          <div className="px-5 py-2"
                            style={{ background:'rgba(46,83,159,.04)', borderBottom:'1px solid var(--border)' }}>
                            <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>
                              {PERM_CATEGORIES[cat] || cat}
                            </p>
                          </div>
                          {perms.map(perm => {
                            const on  = hasGrant(currentRole, perm.code);
                            const key = currentRole.id + perm.code;
                            return (
                              <div key={perm.code}
                                className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-[var(--surface-alt)] transition-colors">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{perm.label}</p>
                                  <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{perm.description}</p>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {saving[key]
                                    ? <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
                                    : <>
                                        <span className="text-xs" style={{ color: on ? '#2E539F' : 'var(--text-secondary)' }}>
                                          {on ? 'Granted' : 'Denied'}
                                        </span>
                                        <Toggle on={on} disabled={currentRole.name === 'Admin'}
                                          onChange={() => togglePermission(currentRole, perm.code, on)}
                                          title={selectedPermLevel === 'Admin' ? 'Admin always has full access' :
                                            (on ? `Revoke on ${activeType}` : `Grant on ${activeType}`)}
                                        />
                                      </>
                                  }
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>

                    <div className="px-5 py-3 border-t text-xs" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
                      {currentRole?.name === 'Admin' ? 'Admin has unrestricted access to all projects.' : `Showing permissions for ${activeType} projects. Changes take effect immediately.`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 2: Page & Module Access ──────────────────────────────── */}

        </>
      )}
    </div>
  );
}
